/**
 * `CropPlate` — a 1px-ruled plate with printer's crop marks and, optionally, the
 * registration crosshair. `docs/design-direction.md` §6.9.
 *
 * The plate is a raised `--bg-raised` panel with a `--border` keyline, `--raised-hi`
 * for the printed-plate lift, and two 8px corner marks drawn as pseudo-elements at
 * the top-left and bottom-right — the global `.plate` recipe in `styles/texture.css`.
 * On top of it this component places the single 12px registration crosshair (a
 * `circle r=5` with a full-width and full-height cross, 1px in the cool state ink)
 * that marks the Now Playing catalogue plate (§8.1).
 *
 * **Nothing else in this system marks a corner** (§6.9): no shadows, no glows, no
 * rounded card. Depth here is three ground steps, 1px rules and the `--raised-hi`
 * inset highlight — never an ambient shadow (§5.5).
 *
 * Typical content is the catalogue plate itself — `BL-0421 / SIDE A / 45 RPM / BPM 92`
 * — which is a `SpecTable`, and which omits every field it does not have rather than
 * printing an em dash (§8.1, §12.6).
 */

import type { JSX, ReactNode } from 'react';

import styles from './primitives.module.css';
import { cx } from './Rule';

export interface CropPlateProps {
  readonly children?: ReactNode;
  /**
   * Draw the 12px registration crosshair at the top right. There is exactly one on a
   * screen — it marks the catalogue plate, and a second one would make it decoration
   * instead of a register mark.
   */
  readonly crosshair?: boolean;
  readonly className?: string | undefined;
}

export function CropPlate({ children, crosshair = false, className }: CropPlateProps): JSX.Element {
  return (
    <div className={cx('plate', styles.crop, className)} data-plate="true">
      {crosshair ? (
        <svg
          className={styles.cross}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="6" cy="6" r="5" />
          <line x1="6" y1="0" x2="6" y2="12" />
          <line x1="0" y1="6" x2="12" y2="6" />
        </svg>
      ) : null}
      {children}
    </div>
  );
}
